import { ChevronRight, House } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type GovernanceUnitNode } from "~/api/types";
import { Badge } from "~/atoms/badge";
import { Button } from "~/atoms/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/atoms/command";
import {
  flattenGovernanceUnits,
  governanceUnitPathIds,
  visibleGovernanceUnits,
} from "~/helpers/governanceUnit";
import { toggleSetItem } from "~/helpers/set";
import { cn } from "~/lib/utils";

const SEARCHABLE_ABOVE = 8;
const INDENT_PX_PER_LEVEL = 12;

interface Props {
  root: GovernanceUnitNode;
  selectedId: number;
  onSelect: (id: number) => void;
}

export function GovernanceUnitList({ root, selectedId, onSelect }: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const query = search.trim();
  const isSearching = query !== "";
  const isSearchable = flattenGovernanceUnits([root]).length > SEARCHABLE_ABOVE;
  // Open down to the selected unit, as the governance units page opens down
  // to the home unit.
  const [expandedIds, setExpandedIds] = useState(
    () => new Set(governanceUnitPathIds(root, selectedId)),
  );

  return (
    <Command shouldFilter={false}>
      {isSearchable && (
        <CommandInput
          placeholder={t(($) => $.commons.search)}
          value={search}
          onValueChange={setSearch}
        />
      )}
      <CommandList showScrollbar>
        <CommandEmpty>{t(($) => $.commons.noItems)}</CommandEmpty>
        <CommandGroup>
          {visibleGovernanceUnits(root, expandedIds, query).map(
            ({ node, depth }) => {
              const isExpandable = !isSearching && node.children.length > 0;

              return (
                <CommandItem
                  key={node.id}
                  value={String(node.id)}
                  data-checked={node.id === selectedId}
                  aria-expanded={
                    isExpandable ? expandedIds.has(node.id) : undefined
                  }
                  className="py-1"
                  // The row expands a node, like a folder in a tree view; a
                  // row with nothing to expand selects instead, so Enter
                  // still picks a leaf or a search hit.
                  onSelect={() =>
                    isExpandable
                      ? setExpandedIds((current) =>
                          toggleSetItem(current, node.id),
                        )
                      : onSelect(node.id)
                  }
                >
                  {!isSearching && (
                    <span
                      className={cn(
                        "flex size-7 items-center justify-center",
                        !isExpandable && "invisible",
                      )}
                      style={{ marginLeft: depth * INDENT_PX_PER_LEVEL }}
                    >
                      <ChevronRight
                        className={cn(
                          "transition-transform",
                          expandedIds.has(node.id) && "rotate-90",
                        )}
                      />
                    </span>
                  )}
                  <Button
                    variant="link"
                    className="text-foreground h-auto min-w-0 shrink p-0 font-normal"
                    title={node.name}
                    onClick={(event) => {
                      event.stopPropagation();
                      onSelect(node.id);
                    }}
                    // Enter would otherwise also reach cmdk, which runs
                    // onSelect of the pointer-selected row.
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.stopPropagation();
                      }
                    }}
                  >
                    <span className="truncate">{node.name}</span>
                  </Button>
                  {node.isHome && (
                    <Badge
                      title={t(($) => $.governanceUnits.yourGovernanceUnit)}
                    >
                      <House className="text-primary-foreground!" />
                    </Badge>
                  )}
                </CommandItem>
              );
            },
          )}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
